import { ReactNode } from "react";
import { Colors } from "../types/colors";
import { getClassName } from "@pairtreefamily/utils";

type BaseTextProps = React.HTMLAttributes<Element> & {
  children: ReactNode;
  className?: string;
  color?: TextColor;
  weight?: TextWeight;
  size?: TextSize;
  family?: TextFamily;
  uppercase?: boolean;
  quote?: boolean;
  SemanticTag?: keyof JSX.IntrinsicElements;
};

type TextFamily = "rebrand" | "gochi-hand" | "montserrat";

const familyMap: Record<TextFamily, string> = {
  rebrand: "font-rebrand",
  "gochi-hand": "font-gochi-hand",
  montserrat: "font-montserrat",
};

type TextSize =
  // body:
  | "text-xs"
  | "text-sm"
  | "text-md"
  | "text-lg"
  | "text-xl"
  // heading:
  | "text-h6"
  | "text-h5"
  | "text-h4"
  | "text-h3"
  | "text-h2"
  | "text-h1"
  // handwriting
  | "text-wsm"
  | "text-wmd"
  | "text-wlg";

// these values come from the tailwind config
// shimming this while text is deprecated
const sizeMap: Record<TextSize, string> = {
  "text-xs": "text-body-4",
  "text-sm": "text-body-3-medium",
  "text-md": "text-body-2-medium",
  "text-lg": "text-body-1",

  "text-xl": "text-head-7",

  "text-h6": "text-head-6",
  "text-h5": "text-head-5",
  "text-h4": "text-head-4",
  "text-h3": "text-head-3",
  "text-h2": "text-head-2",
  "text-h1": "text-head-1",

  "text-wsm": "text-handwriting-3",
  "text-wmd": "text-handwriting-2",
  "text-wlg": "text-handwriting-1",
};

type TextWeight = "light" | "regular" | "medium" | "semibold" | "bold";

const weightMap: Record<TextWeight, string> = {
  light: "font-light",
  regular: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

type TextColor = Colors.Solid;
const colorMap: Record<TextColor, string> = {
  colorless: "", // inherit
  white: "text-white",
  black: "text-black",
  darkblue: "text-darkblue",
  teal: "text-teal",
  green: "text-green",
  lightgreen: "text-lightgreen",
  darkgray: "text-darkgray",
  gray: "text-gray",
  lightgray: "text-lightgray",
  backgroundgray: "text-backgroundgray",
  peach: "text-peach",
  purple: "text-purple",
  rust: "text-rust",
};

export function Text({
  size = "text-md",
  color = "colorless",
  weight = "regular",
  family = "montserrat",
  SemanticTag = "p",
  uppercase, // destructured here so it doesn't get applied to the element
  quote, // destructured here so it doesn't get applied to the element
  className: propsClass,
  ...props
}: BaseTextProps) {
  const className = getClassName(
    sizeMap[size],
    weightMap[weight],
    colorMap[color],
    familyMap[family],
    uppercase && "uppercase",
    quote && "before:content-[open-quote] after:content-[close-quote]",
    propsClass,
  );

  return (
    <SemanticTag className={className} {...props}>
      {props.children}
    </SemanticTag>
  );
}

export type HeadingProps = Omit<BaseTextProps, "size" | "SemanticTag">;

export function Heading1(props: HeadingProps) {
  return (
    <Text
      SemanticTag="h1"
      size="text-h1"
      weight="bold"
      family="rebrand"
      {...props}
    >
      {props.children}
    </Text>
  );
}

export function Heading2(props: HeadingProps) {
  return (
    <Text
      SemanticTag="h2"
      size="text-h2"
      weight="bold"
      family="rebrand"
      {...props}
    >
      {props.children}
    </Text>
  );
}

export function Heading3(props: HeadingProps) {
  return (
    <Text
      SemanticTag="h3"
      size="text-h3"
      weight="bold"
      family="rebrand"
      {...props}
    >
      {props.children}
    </Text>
  );
}

export function Heading4(props: HeadingProps) {
  return (
    <Text
      SemanticTag="h4"
      size="text-h4"
      weight="bold"
      family="rebrand"
      {...props}
    >
      {props.children}
    </Text>
  );
}

export function Heading5(props: HeadingProps) {
  return (
    <Text
      SemanticTag="h5"
      size="text-h5"
      weight="bold"
      family="montserrat"
      {...props}
    >
      {props.children}
    </Text>
  );
}

export function Heading6(props: HeadingProps) {
  return (
    <Text
      SemanticTag="h6"
      size="text-h6"
      weight="bold"
      family="montserrat"
      {...props}
    >
      {props.children}
    </Text>
  );
}

export function Overline(props: HeadingProps) {
  return (
    <Text
      SemanticTag="p"
      size="text-xs"
      weight="bold"
      family="montserrat"
      uppercase
      {...props}
    >
      {props.children}
    </Text>
  );
}

export type BodySize = "xl" | "lg" | "md" | "sm" | "xs";

export type BodyTextProps = Omit<BaseTextProps, "size" | "SemanticTag"> & {
  size?: BodySize;
  SemanticTag: keyof JSX.IntrinsicElements;
};

function translateBodySize(size: BodySize): TextSize {
  switch (size) {
    case "xs":
      return "text-xs";
    case "sm":
      return "text-sm";
    case "md":
      return "text-md";
    case "lg":
      return "text-lg";
    case "xl":
      return "text-xl";
  }
}

export function BodyText({
  size = "md",
  color = "colorless",
  ...props
}: BodyTextProps) {
  return (
    <Text size={translateBodySize(size)} color={color} {...props}>
      {props.children}
    </Text>
  );
}

export type ParagraphProps = Omit<BodyTextProps, "SemanticTag">;

export function Paragraph({ ...props }: ParagraphProps) {
  return (
    <BodyText SemanticTag="p" {...props}>
      {props.children}
    </BodyText>
  );
}

export type InlineProps = Omit<BodyTextProps, "SemanticTag">;

export function Inline(props: InlineProps) {
  return (
    <BodyText SemanticTag="span" {...props}>
      {props.children}
    </BodyText>
  );
}

export type HandwritingSize = "sm" | "md" | "lg";

function translateHandwritingSize(size: HandwritingSize): TextSize {
  switch (size) {
    case "sm":
      return "text-wsm";
    case "md":
      return "text-wmd";
    case "lg":
      return "text-wlg";
  }
}

export type HandwritingProps = Omit<ParagraphProps, "size"> & {
  size?: HandwritingSize;
};

export function Handwriting({ size = "sm", ...props }: HandwritingProps) {
  return (
    <Text family="gochi-hand" size={translateHandwritingSize(size)} {...props}>
      {props.children}
    </Text>
  );
}
